import { Box, Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Client } from "assets/SideBarIcons/customer.svg";
import { useEffect, useMemo, useRef, useState } from "react";
import DynamicSelect from "common/Select/Select";
import ClientTabs from "./ClientTabs";
import Loader from "layout/Loader";
import { ClientProfileService } from "subModule/src/core/services";
import { GetCustomerIntegrationByIdResponse } from "subModule/src/services/client/interface";
import type { CustomerIntegrations } from "subModule/src/services/client/interface";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
import { useGetHostingDetailsService } from "subModule/src/services/client/useClient";
import { useFrozenClientEntityTitle } from "Helpers/useFrozenClientBreadcrumbs";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";
import { buildClientDetailsHref } from "Helpers/customersBreadcrumbs";
import BasicDatePicker from "common/Pickers/Pickers";

interface FormData {
  [key: string]: any;
}

export default function ClientIntegrationModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const loggedInUserData = useSelector(selectUserInfo);
  const entityTitle = useFrozenClientEntityTitle();
  const integrationsListHref = `/client/${params?.id}/account/${params?.accountId}/integrations/invoicingPreference/${params?.invoicingPreference}`;
  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { labelKey: "customers" },
      { labelKey: "customersList", href: "/clients" },
      {
        labelKey: "name",
        ...(entityTitle ? { label: entityTitle } : {}),
        href: buildClientDetailsHref(params as any),
      },
      { labelKey: "integrations", href: integrationsListHref },
      { labelKey: params?.integrationId ? "Details" : "create" },
    ],
    [entityTitle, params, integrationsListHref],
  );
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    reset,
    getValues,
    setValue,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      type: { value: 4, label: t("FleetOS") },
      configValue: {
        accountHostingId: null,
        contractNumber: "",
        contractStart: null,
        contractEnd: null,
      },
    },
  });
  const [loading, setLoading] = useState<boolean>(
    params?.integrationId ? true : false,
  );
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.integrationId ? false : true,
  );
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const pendingHostingIdRef = useRef<number | null>(null);
  const { data: hostingDetailsData, refetch: hostingRefetch } =
    useGetHostingDetailsService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const hostingList = (hostingDetailsData?.pages[0]?.data?.content || []).map(
    (item: any) => ({ value: item.id, label: item.title }),
  );
  const integrationTypes = [{ value: 4, label: t("FleetOS") }];

  useEffect(() => {
    if (params?.integrationId) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.integrationId) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);

  // After hosting list loads, replace raw accountHostingId with title
  useEffect(() => {
    const pendingId = pendingHostingIdRef.current;
    if (pendingId == null) return;

    const applyHostingOption = (option: { value: any; label: string }) => {
      setValue("configValue.accountHostingId", option, {
        shouldDirty: false,
        shouldValidate: true,
      });
      pendingHostingIdRef.current = null;
    };

    const match = hostingList.find(
      (item: any) => Number(item.value) === Number(pendingId),
    );
    if (match?.label) {
      applyHostingOption(match);
      return;
    }

    // List loaded but id not on this page — resolve by id once
    if (hostingDetailsData != null) {
      ClientProfileService.getHostingDetailsById(pendingId)
        .then((byIdRes) => {
          if (pendingHostingIdRef.current !== pendingId) return;
          if (byIdRes?.data?.id != null) {
            applyHostingOption({
              value: byIdRes.data.id,
              label: byIdRes.data.title || String(byIdRes.data.id),
            });
          }
        })
        .catch((err) => console.error("===", err));
    }
  }, [hostingDetailsData, hostingList, setValue]);

  const handleHostingDropdownActions = (
    action: "open" | "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "open") {
      setSearchLoading("hostingDetails");
      hostingRefetch().finally(() => setSearchLoading(null));
      return;
    }
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search || "");
      if (search) setSearchLoading("hostingDetails");
    } else {
      setPageNo((prev) => prev + 1);
      setSearchLoading("hostingDetails");
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      hostingRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };

  const formatDateValue = (value: any) =>
    new Date(value)
      .toLocaleDateString("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
      ?.split("/")
      .reverse()
      .join("-");

  const getByID = () => {
    ClientProfileService.getCustomerIntegrationById(
      +(params as any)?.integrationId,
    )
      .then((res: GetCustomerIntegrationByIdResponse) => {
        const record = (res as any)?.data || (res as any);
        if (res?.success !== false && record) {
          const configValue = record?.configValue || {};
          const rawHostingId = configValue?.accountHostingId;
          if (rawHostingId != null && rawHostingId !== "") {
            pendingHostingIdRef.current = Number(rawHostingId);
          } else {
            pendingHostingIdRef.current = null;
          }
          reset({
            title: record?.title || "",
            description: record?.description || "",
            type: {
              value: record?.type ?? 4,
              label:
                integrationTypes.find((v) => v.value === record?.type)?.label ||
                t("FleetOS"),
            },
            configValue: {
              contractNumber: configValue?.contractNumber || "",
              contractStart: configValue?.contractStart || null,
              contractEnd: configValue?.contractEnd || null,
              accountHostingId:
                rawHostingId != null && rawHostingId !== ""
                  ? {
                      value: Number(rawHostingId),
                      label:
                        configValue.accountHostingTitle ||
                        String(rawHostingId),
                    }
                  : null,
            },
          });
          if (rawHostingId != null && rawHostingId !== "") {
            setSearchLoading("hostingDetails");
            hostingRefetch().finally(() => setSearchLoading(null));
          }
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={(res as any)?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  };

  const buildPayload = (data: any): CustomerIntegrations => ({
    title: data.title,
    type: Number(data.type?.value || 4),
    description: data.description || "",
    // businessId: Number(loggedInUserData?.business?.id),
    customerId: Number(params?.id),
    configValue: {
      contractEnd: data.configValue?.contractEnd || undefined,
      contractStart: data.configValue?.contractStart || undefined,
      contractNumber: data.configValue?.contractNumber || undefined,
      accountHostingId:
        data.configValue?.accountHostingId?.value != null
          ? Number(data.configValue.accountHostingId.value)
          : undefined,
    },
  });

  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    const payload = buildPayload(data);
    if (params?.integrationId) {
      ClientProfileService.updateCustomerIntegration(
        Number(params?.integrationId),
        payload,
      )
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("integration") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(integrationsListHref);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      ClientProfileService.createCustomerIntegration(payload)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("integration") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(integrationsListHref);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(integrationsListHref)}
        onSubmit={handleSubmit(onSubmit)}
        heading={t("customer") + " (" + t("company") + ")"}
        icon={<Client height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
        isBtnEnabled={IsBtnEnabled}
        saveBtnText={params?.integrationId ? "update" : "submit"}
        breadcrumbItems={breadcrumbItems}
      />
      <Box className="flex">
        <ClientTabs params={params} selectedIndex={8} />
        {loading ? (
          <Loader />
        ) : (
          <div className="w-[100%] mt-[10px]">
            <SimpleModal
              visable={true}
              title={
                params?.integrationId
                  ? "editBusinessIntegration"
                  : "addBusinessPolicy"
              }
              isButtonVisable={true}
              isVisableBtn={IsBtnEnabled}
              btnclosetext="Back"
              buttonText={params?.integrationId ? "update" : "submit"}
              attBtnNotshow={false}
              notVisableBackbtn={true}
              showPortList={handleSubmit(onSubmit)}
              formSubmit={handleSubmit(onSubmit)}
              content={
                <Grid
                  container
                  component="form"
                  id="form"
                  rowSpacing={3}
                  columnSpacing={2}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="type"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("type") + " *"}
                          defaultPlaceholder="phSelectIntegrationType"
                          {...field}
                          options={integrationTypes}
                          isSearchable
                          onChange={(selectedOption: any) =>
                            field.onChange(selectedOption)
                          }
                          error={(errors as any)?.type?.message as string}
                          isDisabled={(params as any)?.integrationId}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={9}></Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("title", {
                        validate: {
                          required: (value) =>
                            value?.trim() !== "" ||
                            t("This field is required."),
                        },
                      })}
                      label={t("title") + " *"}
                      placeholder={t("phIntegrationTitle")}
                      title={t("title")}
                      fullWidth
                      error={!!errors.title}
                      helperText={errors.title?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      {...register("description")}
                      label={t("description")}
                      placeholder={t("phIntegrationDescription")}
                      title={t("description")}
                      fullWidth
                      error={!!errors.description}
                      helperText={errors.description?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={1}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      {...register("configValue.contractNumber", {
                        validate: {
                          required: (value) =>
                            value?.trim() !== "" ||
                            t("This field is required."),
                        },
                      })}
                      label={t("contractNumber") + " *"}
                      placeholder={t("phContractNumber")}
                      title={t("contractNumber")}
                      fullWidth
                      error={!!(errors?.configValue as any)?.contractNumber}
                      helperText={
                        (errors?.configValue as any)?.contractNumber
                          ?.message as string
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="configValue.contractStart"
                      control={control}
                      rules={{
                        validate: (value) => {
                          const date = new Date(value);
                          return isNaN(date.getTime()) && value
                            ? t("Invalid Date")
                            : true;
                        },
                      }}
                      render={({ field }) => (
                        <BasicDatePicker
                          type="datePicker"
                          {...field}
                          label={t("contractStartDate")}
                          fullWidth
                          onChange={(value: any) => {
                            field.onChange(formatDateValue(value));
                            trigger("configValue.contractStart");
                          }}
                          required={false}
                          error={
                            (errors?.configValue as any)?.contractStart
                              ?.message as string
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="configValue.contractEnd"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value) return true;
                          const date = new Date(value);
                          const startDate = new Date(
                            getValues("configValue.contractStart"),
                          );
                          if (
                            getValues("configValue.contractStart") &&
                            date < startDate
                          )
                            return t("endDateOrder");
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <BasicDatePicker
                          type="datePicker"
                          {...field}
                          label={t("contractEndDate")}
                          fullWidth
                          onChange={(value: any) => {
                            field.onChange(formatDateValue(value));
                            trigger("configValue.contractEnd");
                          }}
                          required={false}
                          error={
                            (errors?.configValue as any)?.contractEnd
                              ?.message as string
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="configValue.accountHostingId"
                      control={control}
                      defaultValue={null}
                      rules={{ required: t("This field is required.") }}
                      render={({ field }) => (
                        <DynamicSelect
                          label={t("hostingDetails") + " *"}
                          defaultPlaceholder="phSelectHostingDetails"
                          {...field}
                          options={hostingList}
                          isSearchable
                          isClearable
                          onMenuOpen={() =>
                            handleHostingDropdownActions("open")
                          }
                          onChange={(selectedOption: any) =>
                            field.onChange(selectedOption)
                          }
                          error={
                            (errors?.configValue as any)?.accountHostingId
                              ?.message as string
                          }
                          isLoading={searchLoading === "hostingDetails"}
                          onInputChange={(
                            search: string,
                            { action }: { action: string },
                          ) => {
                            if (
                              action === "input-change" ||
                              (action !== "set-value" &&
                                action !== "menu-close" &&
                                action !== "input-blur")
                            )
                              handleHostingDropdownActions("searchIt", search);
                            else if (
                              action === "input-blur" ||
                              action === "menu-close"
                            ) {
                              if (dropdownSearchTerm || pageNo > 1)
                                handleHostingDropdownActions("searchIt", "");
                              else setDropdownSearchTerm("");
                              setPageNo(1);
                            }
                          }}
                          onMenuScrollToBottom={() => {
                            if (
                              !(hostingDetailsData?.pages[0]?.data as any)?.last
                            )
                              handleHostingDropdownActions("loadNext");
                          }}
                          page={pageNo}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              }
            />
          </div>
        )}
      </Box>
    </>
  );
}
